import React, { useState, useMemo } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Link } from "react-router-dom";
import { ArrowRight, Search, Filter, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export interface ProductCard {
  id: string;
  name: string;
  desc: string;
  category: string;
  status: 'live' | 'beta' | 'building';
}

const allProducts: ProductCard[] = [
  // AI Email (5)
  { id: 'lifemail', name: 'lifemail', desc: 'AI email client with memory. Remembers every contact and drafts replies in your tone.', category: 'AI Email', status: 'beta' },
  { id: 'coldmail-ai', name: 'ColdMail AI', desc: 'Personalized cold emails with company-specific hooks. For job seekers and sales.', category: 'AI Email', status: 'live' },
  { id: 'follow-up-sequence', name: 'Sequence Writer', desc: 'Full sales cadences that adapt tone from curious to urgent. Never lose a lead.', category: 'AI Email', status: 'live' },
  { id: 'subject-line-optimizer', name: 'Subject Line Optimizer', desc: 'Generate and score subject lines by predicted open rate before sending.', category: 'AI Email', status: 'beta' },
  { id: 'inbox-zero-coach', name: 'Inbox Zero Coach', desc: 'Triage, batch, and clear your inbox with AI-suggested actions. No more decision fatigue.', category: 'AI Email', status: 'building' },

  // Lead Generation (5)
  { id: 'linkedin-lead-scraper', name: 'Lead Qualifier', desc: 'Find and score prospects on LinkedIn. Role, company size, activity signals.', category: 'Lead Generation', status: 'live' },
  { id: 'local-business-finder', name: 'Local Lead Finder', desc: 'Find trades and local services by location. Extracts contacts and writes outreach.', category: 'Lead Generation', status: 'live' },
  { id: 'email-finder-verifier', name: 'Email Verifier', desc: 'Guess corporate emails from name + domain. Verify deliverability before sending.', category: 'Lead Generation', status: 'beta' },
  { id: 'competitor-monitor', name: 'Competitor Monitor', desc: 'Track competitor websites and job boards for changes. Know the market before you move.', category: 'Lead Generation', status: 'building' },
  { id: 'review-response-generator', name: 'Review Response Generator', desc: 'Turn Google and Yelp reviews into professional, on-brand responses instantly.', category: 'Lead Generation', status: 'building' },

  // Content (4)
  { id: 'blog-post-generator', name: 'Blog Post Generator', desc: 'Long-form SEO content from briefs. Headers, body, meta — the full draft.', category: 'Content', status: 'beta' },
  { id: 'linkedin-post-writer', name: 'LinkedIn Post Writer', desc: 'Executive ghostwriting from bullet points. Hook, story, CTA. Ready to post.', category: 'Content', status: 'beta' },
  { id: 'newsletter-builder', name: 'Newsletter Builder', desc: 'Curate newsletters from RSS feeds and notes. Structure, format, send-ready HTML.', category: 'Content', status: 'building' },
  { id: 'faq-builder', name: 'FAQ Builder', desc: 'Turn support tickets into a self-service knowledge base. Reduces volume without hiring.', category: 'Content', status: 'building' },

  // Documents (4)
  { id: 'contract-scanner', name: 'Contract Scanner', desc: 'Flag risky clauses before you sign. Not legal advice — just a second pair of eyes.', category: 'Documents', status: 'beta' },
  { id: 'resume-parser', name: 'Resume Parser', desc: 'Extract skills, experience, and education from any resume format. ATS-ready output.', category: 'Documents', status: 'live' },
  { id: 'proposal-writer', name: 'Proposal Writer', desc: 'Turn a client brief into a professional proposal. Scope, timeline, pricing, terms.', category: 'Documents', status: 'building' },
  { id: 'pdf-summarizer', name: 'PDF Summarizer', desc: 'Understand any 500-page document in 60 seconds. Key findings, risks, action items.', category: 'Documents', status: 'beta' },

  // Voice & Meeting (2)
  { id: 'meeting-notes-ai', name: 'Meeting Notes AI', desc: 'Transcribe, summarize, and extract action items. No more "what did we agree on?"', category: 'Voice & Meeting', status: 'beta' },
  { id: 'voice-memo-transcriber', name: 'Voice Memo Transcriber', desc: 'Turn voice notes into structured, searchable text. Topics, tasks, and ideas.', category: 'Voice & Meeting', status: 'building' },

  // Social (2)
  { id: 'social-media-scheduler', name: 'Social Media Scheduler', desc: 'Queue, schedule, and auto-post across platforms. With analytics on what performs.', category: 'Social', status: 'building' },
  { id: 'hashtag-researcher', name: 'Hashtag Researcher', desc: 'Find hashtags that actually reach people. Not just the most crowded ones.', category: 'Social', status: 'building' },

  // Operations (4)
  { id: 'data-cleaner', name: 'Data Cleaner', desc: 'Fix messy CSVs, dedupe, and standardize in one pass. The client data you actually get.', category: 'Operations', status: 'live' },
  { id: 'invoice-generator', name: 'Invoice Generator', desc: 'From time entries to professional invoice in 30 seconds. Branded PDF, ready to send.', category: 'Operations', status: 'live' },
  { id: 'api-tester', name: 'API Tester', desc: 'Generate test cases from your API spec. Catch edge cases before production.', category: 'Operations', status: 'beta' },
  { id: 'feature-request-prioritizer', name: 'Feature Prioritizer', desc: 'Cluster requests by theme, score by impact and effort. Build what matters.', category: 'Operations', status: 'building' },

  // Niche (2)
  { id: 'onboarding-email-sequence', name: 'Onboarding Sequence', desc: 'Turn trial users into paying customers with behavior-triggered emails.', category: 'Niche', status: 'building' },
  { id: 'churn-prediction-dashboard', name: 'Churn Dashboard', desc: 'Predict churn risk from behavior patterns. Prevent cancellations before they happen.', category: 'Niche', status: 'building' },
];

const categories = [
  'All',
  'AI Email',
  'Lead Generation',
  'Content',
  'Documents',
  'Voice & Meeting',
  'Social',
  'Operations',
  'Niche'
];

const statusOrder = { live: 0, beta: 1, building: 2 };
const statusDot = (status: string) => {
  const map: Record<string, string> = {
    live: 'bg-emerald-400',
    beta: 'bg-amber-400',
    building: 'bg-slate-400'
  };
  return <span className={`h-2 w-2 rounded-full ${map[status] || map.building}`} />;
};

const statusLabel = (status: string) => {
  const map: Record<string, string> = { live: 'Live', beta: 'Beta', building: 'Building' };
  return map[status] || status;
};

const Projects = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const filtered = useMemo(() => {
    let list = allProducts;
    if (activeCategory !== 'All') {
      list = list.filter(p => p.category === activeCategory);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(p =>
        p.name.toLowerCase().includes(q) ||
        p.desc.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q) ||
        p.id.toLowerCase().includes(q)
      );
    }
    // Sort: live first, then beta, then building
    return [...list].sort((a, b) => statusOrder[a.status] - statusOrder[b.status]);
  }, [activeCategory, searchQuery]);

  const stats = useMemo(() => {
    return {
      total: allProducts.length,
      live: allProducts.filter(p => p.status === 'live').length,
      beta: allProducts.filter(p => p.status === 'beta').length,
      building: allProducts.filter(p => p.status === 'building').length
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-dark-500">
      <Navbar />
      <main className="flex-grow pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Our Products</h1>
            <p className="text-gray-400 text-lg max-w-2xl mb-6">
              28 focused tools across 7 categories. Each one solves a single job end to end. 
              No platforms. No bloat. Just working products.
            </p>

            <div className="flex flex-wrap gap-3">
              <div className="glass-card px-4 py-2 rounded-full flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-emerald-400" />
                <span className="text-sm text-gray-300">{stats.live} Live</span>
              </div>
              <div className="glass-card px-4 py-2 rounded-full flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-amber-400" />
                <span className="text-sm text-gray-300">{stats.beta} Beta</span>
              </div>
              <div className="glass-card px-4 py-2 rounded-full flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-slate-400" />
                <span className="text-sm text-gray-300">{stats.building} Building</span>
              </div>
            </div>
          </div>

          {/* Search + Filter */}
          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            <div className="relative flex-grow max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              <Input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-dark-400/50 border-dark-300 text-white placeholder:text-gray-500"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Category tabs */}
          <div className="flex flex-wrap gap-2 mb-10">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeCategory === cat
                    ? 'bg-tasktide-teal/20 text-tasktide-teal border border-tasktide-teal/30'
                    : 'bg-dark-400/50 text-gray-400 border border-white/5 hover:text-white hover:border-white/10'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Product grid */}
          {filtered.length === 0 ? (
            <div className="text-center py-20">
              <Filter className="h-10 w-10 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">No products match your search.</p>
              <Button
                variant="ghost"
                onClick={() => { setSearchQuery(''); setActiveCategory('All'); }}
                className="mt-4 text-tasktide-teal hover:text-tasktide-teal/80"
              >
                Clear filters
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((product) => (
                <Link
                  to={`/projects/${product.id}`}
                  key={product.id}
                  className="group block"
>
                  <div className="glass-card p-6 rounded-xl h-full transition-all duration-300 hover:shadow-xl hover:shadow-tasktide-teal/5 hover:-translate-y-0.5 border border-white/5">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs text-gray-500">{product.category}</span>
                      <div className="flex items-center gap-1.5">
                        {statusDot(product.status)}
                        <span className="text-xs text-gray-400">{statusLabel(product.status)}</span>
                      </div>
                    </div>

                    <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-tasktide-teal transition-colors">
                      {product.name}
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed mb-4">{product.desc}</p>

                    <div className="flex items-center text-tasktide-teal text-sm opacity-0 group-hover:opacity-100 transition-opacity">
                      Learn more <ArrowRight className="h-3.5 w-3.5 ml-1" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Projects;